/**
 * EducationScholarshipNew — /education/scholarships/new
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Award, Save } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import EducationCategoryNav from "@/components/EducationCategoryNav";
import VideoLinkManager from "@/components/VideoLinkManager";

const empty = {
  name: "", provider: "", category: "", level: "",
  description: "", country: "", state: "",
  amount_summary: "", currency: "USD",
  application_deadline: "", application_url: "", contact_email: "",
  eligibility: "", requirements: "", cover_image_url: "",
};

export default function EducationScholarshipNew() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [videoUrls, setVideoUrls] = useState([]);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { http.get("/education/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!authLoading && !user) {
      toast.error("Sign in to post a scholarship.");
      nav(`/login?redirect=${encodeURIComponent("/education/scholarships/new")}`, { replace: true });
    }
  }, [authLoading, user, nav]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.provider.trim() || !form.category) {
      toast.error("Name, provider and category are required."); return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        provider: form.provider.trim(),
        category: form.category,
        level: form.level || null,
        description: form.description || null,
        country: form.country || null,
        state: form.state || null,
        amount_summary: form.amount_summary || null,
        currency: form.currency || "USD",
        application_deadline: form.application_deadline || null,
        application_url: form.application_url || null,
        contact_email: form.contact_email || null,
        eligibility: form.eligibility || null,
        requirements: form.requirements || null,
        cover_image_url: form.cover_image_url || null,
        video_urls: videoUrls,
      };
      const r = await http.post("/education/scholarships", payload);
      toast.success("Scholarship published!");
      nav(`/education/scholarships/${r.data.scholarship.slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create scholarship.");
    } finally { setSaving(false); }
  };

  return (
    <>
      <EducationCategoryNav />
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="scholarship-new-page">
        <div className="overline mb-1">NEW SCHOLARSHIP</div>
        <h1 className="font-display text-4xl mb-2 inline-flex items-center gap-2">
          <Award className="w-8 h-8" />Post a scholarship
        </h1>
        <p className="opacity-70 mb-6">Reach students who match your criteria — merit, need, sports, arts and more.</p>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Scholarship name *">
            <input required value={form.name} onChange={e => set("name", e.target.value)} className="wofy-input" data-testid="input-name" />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Provider *"><input required value={form.provider} onChange={e => set("provider", e.target.value)} className="wofy-input" data-testid="input-provider" /></Field>
            <Field label="Category *">
              <select required value={form.category} onChange={e => set("category", e.target.value)} className="wofy-input" data-testid="input-category">
                <option value="">Select…</option>
                {meta?.scholarship_categories?.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Level (who it's for)">
              <select value={form.level} onChange={e => set("level", e.target.value)} className="wofy-input" data-testid="input-level">
                <option value="">Any</option>
                {meta?.institution_levels?.map(l => <option key={l.slug} value={l.slug}>{l.label}</option>)}
              </select>
            </Field>
            <Field label="Country"><input value={form.country} onChange={e => set("country", e.target.value)} className="wofy-input" data-testid="input-country" /></Field>
            <Field label="State"><input value={form.state} onChange={e => set("state", e.target.value)} className="wofy-input" data-testid="input-state" /></Field>
          </div>

          <Field label="Description">
            <textarea rows={4} value={form.description} onChange={e => set("description", e.target.value)} className="wofy-input" data-testid="input-description" />
          </Field>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Amount summary"><input value={form.amount_summary} onChange={e => set("amount_summary", e.target.value)} placeholder="e.g. Up to $5,000/year" className="wofy-input" data-testid="input-amount" /></Field>
            <Field label="Currency"><input value={form.currency} onChange={e => set("currency", e.target.value)} maxLength={4} className="wofy-input" data-testid="input-currency" /></Field>
            <Field label="Application deadline"><input type="date" value={form.application_deadline} onChange={e => set("application_deadline", e.target.value)} className="wofy-input" data-testid="input-deadline" /></Field>
          </div>

          <Field label="Eligibility criteria">
            <textarea rows={3} value={form.eligibility} onChange={e => set("eligibility", e.target.value)} className="wofy-input" data-testid="input-eligibility" />
          </Field>
          <Field label="Requirements (documents, essays, references…)">
            <textarea rows={3} value={form.requirements} onChange={e => set("requirements", e.target.value)} className="wofy-input" data-testid="input-requirements" />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Application URL"><input value={form.application_url} onChange={e => set("application_url", e.target.value)} className="wofy-input" data-testid="input-appurl" /></Field>
            <Field label="Contact email"><input type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} className="wofy-input" data-testid="input-email" /></Field>
          </div>

          <Field label="Cover image URL"><input value={form.cover_image_url} onChange={e => set("cover_image_url", e.target.value)} className="wofy-input" data-testid="input-cover" /></Field>

          <VideoLinkManager value={videoUrls} onChange={setVideoUrls} max={5}
            helper="Optional: paste a YouTube or Vimeo intro video." />

          <div className="pt-2">
            <button disabled={saving} className="wofy-btn-primary inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Publish scholarship
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">{label}</div>
      {children}
    </label>
  );
}
